import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { TrendingUp, Users, CreditCard, Award, Activity, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface StatisticsData {
  totalAdmins: number;
  activeAdmins: number;
  totalCustomers: number;
  totalStamps: number;
  totalReferrals: number;
  monthlyRevenue: number;
  planDistribution: Array<{ name: string; value: number; color: string }>;
  monthlyGrowth: Array<{ month: string; admins: number; customers: number; revenue: number }>;
  leaderboard: Array<{
    tenant_name: string;
    customer_count: number;
    referral_count: number;
    stamp_count: number;
  }>;
}

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

export function StatisticsModule() {
  const [data, setData] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30d");
  const { toast } = useToast();

  useEffect(() => {
    fetchStatistics();
  }, [timeRange]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      
      // Fetch basic counts
      const [tenantsRes, customersRes, stampsRes, revenueRes] = await Promise.all([
        supabase.from("tenants").select("id, name, status, created_at"),
        supabase.from("customers").select("id, tenant_id, created_at"),
        supabase.from("stamps").select("id, tenant_id, stamp_type, created_at"),
        supabase.from("stripe_payments").select("amount, created_at, tenant_id"),
      ]);

      if (tenantsRes.error) throw tenantsRes.error;
      if (customersRes.error) throw customersRes.error;
      if (stampsRes.error) throw stampsRes.error;
      if (revenueRes.error) throw revenueRes.error;

      const tenants = tenantsRes.data || [];
      const customers = customersRes.data || [];
      const stamps = stampsRes.data || [];
      const payments = revenueRes.data || [];

      // Calculate basic stats
      const totalAdmins = tenants.length;
      const activeAdmins = tenants.filter(t => t.status === "active").length;
      const totalCustomers = customers.length;
      const totalStamps = stamps.length;
      const totalReferrals = stamps.filter(s => s.stamp_type === "referral").length;
      const monthlyRevenue = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

      // Fetch subscription plan distribution
      const { data: subscriptions, error: subError } = await supabase
        .from("tenant_subscriptions")
        .select(`
          subscription_plans (name),
          tenant_id
        `);

      if (subError) throw subError;

      const planCounts: { [key: string]: number } = {};
      (subscriptions || []).forEach(sub => {
        const planName = sub.subscription_plans?.name || "Unbekannt";
        planCounts[planName] = (planCounts[planName] || 0) + 1;
      });

      const planDistribution = Object.entries(planCounts).map(([name, value], index) => ({
        name,
        value,
        color: COLORS[index % COLORS.length],
      }));

      // Calculate monthly growth (last 6 months)
      const now = new Date();
      const monthlyGrowth = [];
      
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        
        const monthAdmins = tenants.filter(t => 
          new Date(t.created_at) >= monthDate && new Date(t.created_at) < nextMonth
        ).length;
        
        const monthCustomers = customers.filter(c => 
          new Date(c.created_at) >= monthDate && new Date(c.created_at) < nextMonth
        ).length;
        
        const monthRevenue = payments.filter(p => 
          new Date(p.created_at) >= monthDate && new Date(p.created_at) < nextMonth
        ).reduce((sum, p) => sum + (p.amount || 0), 0);

        monthlyGrowth.push({
          month: monthDate.toLocaleDateString("de-DE", { month: "short", year: "2-digit" }),
          admins: monthAdmins,
          customers: monthCustomers,
          revenue: monthRevenue,
        });
      }

      // Create leaderboard by tenant
      const tenantStats: { [key: string]: any } = {};
      
      tenants.forEach(tenant => {
        tenantStats[tenant.id] = {
          tenant_name: tenant.name,
          customer_count: customers.filter(c => c.tenant_id === tenant.id).length,
          referral_count: stamps.filter(s => s.tenant_id === tenant.id && s.stamp_type === "referral").length,
          stamp_count: stamps.filter(s => s.tenant_id === tenant.id).length,
        };
      });

      const leaderboard = Object.values(tenantStats)
        .sort((a: any, b: any) => b.customer_count - a.customer_count)
        .slice(0, 10);

      setData({
        totalAdmins,
        activeAdmins,
        totalCustomers,
        totalStamps,
        totalReferrals,
        monthlyRevenue,
        planDistribution,
        monthlyGrowth,
        leaderboard,
      });

    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast({
        title: "Fehler",
        description: "Statistiken konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Statistiken & Monitoring</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Statistiken & Monitoring
        </CardTitle>
        <CardDescription>
          Überwachen Sie Plattform-Performance und Wachstum
        </CardDescription>
        <div className="flex justify-end">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-48">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Letzte 7 Tage</SelectItem>
              <SelectItem value="30d">Letzte 30 Tage</SelectItem>
              <SelectItem value="90d">Letzte 90 Tage</SelectItem>
              <SelectItem value="1y">Letztes Jahr</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gradient-to-r from-blue-500/10 to-blue-600/10 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">Admins</span>
            </div>
            <div className="text-2xl font-bold">{data.totalAdmins}</div>
            <div className="text-xs text-muted-foreground">
              {data.activeAdmins} aktiv
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500/10 to-green-600/10 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-600">Kunden</span>
            </div>
            <div className="text-2xl font-bold">{data.totalCustomers}</div>
            <div className="text-xs text-muted-foreground">
              Registriert
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500/10 to-purple-600/10 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center gap-2 mb-2">
              <Award className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-600">Stempel</span>
            </div>
            <div className="text-2xl font-bold">{data.totalStamps}</div>
            <div className="text-xs text-muted-foreground">
              Ausgegeben
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 rounded-lg p-4 border border-orange-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium text-orange-600">Empfehlungen</span>
            </div>
            <div className="text-2xl font-bold">{data.totalReferrals}</div>
            <div className="text-xs text-muted-foreground">
              Generiert
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 rounded-lg p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-600">Umsatz</span>
            </div>
            <div className="text-2xl font-bold">{data.monthlyRevenue.toFixed(2)}€</div>
            <div className="text-xs text-muted-foreground">
              Diesen Monat
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Growth Chart */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4">Monatliches Wachstum</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.monthlyGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="admins" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  name="Neue Admins"
                />
                <Line 
                  type="monotone" 
                  dataKey="customers" 
                  stroke="hsl(var(--secondary))" 
                  strokeWidth={2}
                  name="Neue Kunden"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Plan Distribution */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4">Preisplan-Verteilung</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.planDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {data.planDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {data.planDistribution.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="bg-muted/30 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4">Umsatzentwicklung</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.monthlyGrowth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`${value}€`, "Umsatz"]} />
              <Bar 
                dataKey="revenue" 
                fill="hsl(var(--primary))" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Performers Leaderboard */}
        <div className="bg-muted/30 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4">Top Performance Leaderboard</h3>
          <div className="space-y-3">
            {data.leaderboard.map((tenant, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-background rounded border">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium">{tenant.tenant_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {tenant.customer_count} Kunden • {tenant.referral_count} Empfehlungen
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{tenant.stamp_count}</div>
                  <div className="text-xs text-muted-foreground">Stempel</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}